/*
 * CAPTURE-OWNED MODULE
 * MailboxStepDialog — which mailbox this channel watches.
 *
 * THE STEP BETWEEN CONSENT AND THE FOLDER PICKER, and it exists because a
 * mailbox connection has a question a drive connection does not. OneDrive
 * resolves /me/drive on the way back from Microsoft: the target is decided by
 * whose account authorised it. A mail consent grants access to the user's own
 * mailbox AND to every shared mailbox they hold rights to, so the account alone
 * does not say which one to watch. Somebody has to be asked, after consent,
 * because the list of shared mailboxes is not knowable before it.
 *
 * IT IS NOT A SETTINGS FIELD, and that is why it is not part of the card's
 * Import settings block. The server route behind it does two things a settings
 * write must never do blindly: it proves access with a live Graph call before
 * writing anything, and it stamps the bind-time cutoff that decides which mail
 * is old enough to ignore. Both belong to the act of binding.
 *
 * ERRORS ARE SHOWN VERBATIM. "That mailbox was not found" and "You do not have
 * access to that mailbox" are different problems with different fixes — a typo
 * versus an admin needing to grant full access — and the server distinguishes
 * them. Collapsing both into "could not save" would send half of the people who
 * see it to fix the wrong thing.
 */

import React, { useCallback, useState } from 'react';
import { captureAPI } from '../services/api';
import './MailboxStepDialog.css';

/**
 * @param {object}   channel    the outlook_mailbox channel being configured
 * @param {Function} onClose    called with no argument on cancel
 * @param {Function} onBound    called with the updated channel row on success
 */
export default function MailboxStepDialog({ channel, onClose, onBound }) {
  // Pre-filled from the row when there is one, so re-opening this on a bound
  // channel shows what it is bound to rather than an empty form that looks like
  // the binding was lost.
  const [kind, setKind] = useState(channel?.mailbox_kind || 'shared');
  // A 'self' mailbox stores the connected account's own address. That is not a
  // shared mailbox someone typed, and must not prefill the shared field.
  const [address, setAddress] = useState(
    channel?.mailbox_kind === 'shared' ? channel?.mailbox_address || '' : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  const isRebind = Boolean(channel?.mailbox_kind);

  const submit = useCallback(async () => {
    setSaving(true);
    setError(null);
    setErrorCode(null);
    try {
      const res = await captureAPI.ingestion.setMailbox(channel.id, { kind, address });
      if (onBound) onBound(res?.channel || null, { rebound: res?.rebound === true });
      onClose();
    } catch (err) {
      // The server's own sentence. Its 400s name the mailbox problem precisely
      // and its 502 says the problem is Microsoft rather than the address — a
      // paraphrase here would flatten that back into one unhelpful message.
      setError(err?.response?.data?.error || 'That mailbox could not be saved.');
      setErrorCode(err?.response?.data?.code || null);
    } finally {
      setSaving(false);
    }
  }, [channel, kind, address, onBound, onClose]);

  // The address box is the only required field, and only for a shared mailbox.
  // Trimmed here because the server trims too and a value that differs only by
  // whitespace would look rejected for no visible reason.
  const canSubmit = !saving && (kind === 'self' || address.trim().length > 0);

  return (
    <div className="msd-overlay" onClick={() => (saving ? null : onClose())}>
      <div className="msd-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="msd-head">
          <h3 className="msd-title">Which mailbox should we watch?</h3>
          <button className="msd-close" onClick={onClose} disabled={saving} aria-label="Close">
            &times;
          </button>
        </div>

        <p className="msd-copy">
          Documents will be imported from this mailbox. You can choose which folder inside it
          to watch in the next step.
        </p>

        <div className="msd-choices">
          <label className="msd-choice">
            <input
              type="radio"
              name="msd-kind"
              value="self"
              checked={kind === 'self'}
              disabled={saving}
              onChange={() => setKind('self')}
            />
            <span className="msd-choice-body">
              <span className="msd-choice-title">The connected account&rsquo;s mailbox</span>
              <span className="msd-choice-note">
                The mailbox of the Microsoft 365 account this workspace is connected as.
              </span>
            </span>
          </label>

          <label className="msd-choice">
            <input
              type="radio"
              name="msd-kind"
              value="shared"
              checked={kind === 'shared'}
              disabled={saving}
              onChange={() => setKind('shared')}
            />
            <span className="msd-choice-body">
              <span className="msd-choice-title">A shared mailbox</span>
              <span className="msd-choice-note">
                An address such as invoices@yourcompany.com. You need full access to it.
              </span>
            </span>
          </label>
        </div>

        {/* Rendered only for 'shared'. Present-but-disabled would invite an
            address to be typed and then silently dropped — for the connected
            account's own mailbox the server takes the address from the
            connection and ignores anything sent, so a filled box that does
            nothing is a lie about what will be saved. */}
        {kind === 'shared' && (
          <div className="msd-field">
            <label className="msd-field-label" htmlFor="msd-address">
              Mailbox address
            </label>
            <input
              id="msd-address"
              className="msd-input"
              type="email"
              autoComplete="off"
              placeholder="invoices@yourcompany.com"
              value={address}
              disabled={saving}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>
        )}

        {/* Only when there is an existing binding to lose. Shown BEFORE the
            action, not after: the watch folder and the cursor belong to the old
            mailbox and are cleared by the save, and finding that out afterwards
            reads as a bug. */}
        {isRebind && (
          <p className="msd-warn">
            Changing the mailbox clears the watched folder and the import history for this
            source. You will need to choose a folder again, and moving handled mail is turned
            off until you switch it back on.
          </p>
        )}

        {error && (
          <div className="msd-error" role="alert">
            <span className="msd-error-msg">{error}</span>
            {errorCode && <span className="msd-error-code">{errorCode}</span>}
          </div>
        )}

        <div className="msd-actions">
          <button className="msd-btn msd-btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="msd-btn msd-btn-save" onClick={submit} disabled={!canSubmit}>
            {saving ? 'Checking mailbox…' : 'Use this mailbox'}
          </button>
        </div>
      </div>
    </div>
  );
}
